import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  Cached as CachedIcon,
  Webhook as WebhookIcon,
  CloudSync as CloudSyncIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { db, httpsCallable, functions, collection, query, orderBy, limit, onSnapshot } from './firebaseConfig';

const XeroLogsDialog = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('webhooks');
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [refreshLogs, setRefreshLogs] = useState([]);
  const [loading, setLoading] = useState({
    webhooks: false,
    syncs: false,
    refreshes: false,
    manualSync: false
  });
  const [manualSyncDialogOpen, setManualSyncDialogOpen] = useState(false);
  const [manualSyncMonths, setManualSyncMonths] = useState(1);
  const [manualSyncResult, setManualSyncResult] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [unsubscribes, setUnsubscribes] = useState({
    webhooks: null,
    syncs: null,
    refreshes: null
  });
  
  // Use a ref to track the current unsubscribes without causing re-renders
  const unsubscribesRef = useRef(unsubscribes);
  useEffect(() => {
    unsubscribesRef.current = unsubscribes;
  }, [unsubscribes]);

  const fetchLogs = useCallback((type) => {
    try {
      setLoading(prev => ({ ...prev, [type]: true }));
      
      let queryRef = query(
        collection(db, 'xero-details', 'logs', type),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      
      // Use the ref to access the current value of unsubscribes
      if (unsubscribesRef.current[type]) {
        unsubscribesRef.current[type]();
      }
      
      const unsubscribe = onSnapshot(queryRef, snapshot => {
        const logsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            type: type
          };
        });
        
        switch (type) {
          case 'webhooks':
            setWebhookLogs(logsData);
            break;
          case 'syncs':
            setSyncLogs(logsData);
            break;
          case 'refreshes':
            setRefreshLogs(logsData);
            break;
          default:
            console.warn(`Unknown log type: ${type}`);
            break;
        }
        
        setLoading(prev => ({ ...prev, [type]: false }));
      }, error => {
        console.error(`Error in snapshot listener for ${type} logs:`, error);
        setLoading(prev => ({ ...prev, [type]: false }));
      });
      
      setUnsubscribes(prev => ({
        ...prev,
        [type]: unsubscribe
      }));
      
      return unsubscribe;
    } catch (error) {
      console.error(`Error setting up snapshot for ${type} logs:`, error);
      setLoading(prev => ({ ...prev, [type]: false }));
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllLogs = () => {
    // Set loading state for all tabs when explicitly refreshing all logs
    setLoading(prev => ({ ...prev, webhooks: true, syncs: true, refreshes: true }));
    
    try {
      fetchLogs('webhooks');
      fetchLogs('syncs');
      fetchLogs('refreshes');
    } catch (error) {
      console.error('Error setting up log listeners:', error);
      setLoading(prev => ({ ...prev, webhooks: false, syncs: false, refreshes: false }));
    }
  };

  const handleManualSyncOpen = () => {
    setManualSyncDialogOpen(true);
    setManualSyncResult(null);
  };

  const handleManualSyncClose = () => {
    setManualSyncDialogOpen(false);
  };

  const handleManualSyncChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 24) {
      setManualSyncMonths(value);
    }
  };

  const triggerManualSync = async () => {
    setLoading(prev => ({ ...prev, manualSync: true }));
    setManualSyncResult(null);
    
    try {
      const syncFunction = httpsCallable(functions, 'syncXeroInvoicesCustom');
      const result = await syncFunction({ months: manualSyncMonths });
      
      setManualSyncResult({
        success: true,
        message: result.data.message || 'Sync started successfully'
      });
      
      // Refresh the syncs tab to show the new sync
      if (activeTab === 'syncs') {
        fetchLogs('syncs');
      }
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      setManualSyncResult({
        success: false,
        message: error.message || 'An error occurred during sync'
      });
    } finally {
      setLoading(prev => ({ ...prev, manualSync: false }));
    }
  };

  useEffect(() => {
    if (open) {
      // Only set loading state for the active tab
      setLoading(prev => ({ ...prev, [activeTab]: true }));
      fetchLogs(activeTab);
    }
  }, [open, activeTab, fetchLogs]);
  


  useEffect(() => {
    return () => {
      if (unsubscribes && typeof unsubscribes === 'object') {
        Object.values(unsubscribes).forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
    };
  }, [unsubscribes]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCurrentLogs = () => {
    switch (activeTab) {
      case 'webhooks': return webhookLogs;
      case 'syncs': return syncLogs;
      case 'refreshes': return refreshLogs;
      default: return [];
    }
  };



  const currentLogs = getCurrentLogs();
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - currentLogs.length) : 0;
  const isLoading = loading[activeTab];

  const renderStatusChip = (status) => {
    if (!status) return null;
    
    const statusLower = status.toLowerCase();
    let color = 'default';
    
    if (statusLower.includes('success') || statusLower === 'completed') {
      color = 'success';
    } else if (statusLower.includes('error') || statusLower === 'failed') {
      color = 'error';
    } else if (statusLower.includes('warning')) {
      color = 'warning';
    } else if (statusLower.includes('pending') || statusLower === 'started') {
      color = 'info';
    }
    
    return (
      <Chip 
        label={status} 
        color={color} 
        size="small"
        sx={{ 
          textTransform: 'capitalize',
          fontWeight: 500,
          minWidth: 80,
          justifyContent: 'center'
        }}
      />
    );
  };

  const renderLogDetails = (log) => {
    let timestamp = null;
    if (log.timestamp?.toDate) {
      timestamp = log.timestamp.toDate();
    } else if (log.timestamp?.seconds) {
      timestamp = new Date(log.timestamp.seconds * 1000);
    } else if (log.timestamp) {
      timestamp = new Date(log.timestamp);
    }
    
    const renderRefreshLog = () => {
      const isSuccess = log.success === true;
      const status = isSuccess ? 'Success' : 'Failed';
      
      return {
        title: isSuccess ? 'Token Refresh' : 'Token Refresh Failed',
        status: status,
        details: isSuccess 
          ? 'Xero token was refreshed successfully' 
          : `Error: ${log.error || 'Unknown error'}`,
        color: isSuccess ? 'success' : 'error'
      };
    };

    const renderSyncLog = () => {
      const status = log.status?.toLowerCase() || 'unknown';
      let details = [];
      
      if (status === 'completed') {
        details.push(`Synced ${log.totalInvoices || 0} invoices`);
        if (log.executionTimeSeconds) {
          details.push(`in ${log.executionTimeSeconds}s`);
        }
      } else if (status === 'failed') {
        details.push('Sync failed');
        if (log.error) details.push(log.error);
      } else if (status === 'started') {
        details.push('Sync started');
      }
      
      return {
        title: 'Invoice Sync',
        status: status,
        details: details.join(' • '),
        color: status === 'completed' ? 'success' : 
               status === 'failed' ? 'error' : 'info'
      };
    };

    const renderWebhookLog = () => {
      const eventType = log.event?.eventType || 'Unknown';
      const status = log.success === false ? 'failed' : 'delivered';
      const details = [eventType];
      
      if (log.invoiceNumber && log.invoiceNumber !== 'unknown') {
        details.push(`Invoice: ${log.invoiceNumber}`);
      }
      
      if (log.error) {
        details.push(`Error: ${log.error}`);
      }
      
      return {
        title: 'Webhook Event',
        status: status,
        details: details.join(' • '),
        color: status === 'failed' ? 'error' : 'info'
      };
    };

    let logData;
    switch (log.type) {
      case 'refreshes':
        logData = renderRefreshLog();
        break;
      case 'syncs':
        logData = renderSyncLog();
        break;
      case 'webhooks':
      default:
        logData = renderWebhookLog();
    }

    if (isMobile) {
      return (
        <Card variant="outlined" sx={{ mb: 1, borderLeft: `4px solid ${theme.palette[logData.color]?.main || theme.palette.primary.main}` }}>
          <CardHeader
            title={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">
                  {logData.title}
                </Typography>
                {renderStatusChip(logData.status)}
              </Box>
            }
            subheader={
              <Typography variant="caption" color="text.secondary">
                {timestamp ? format(timestamp, 'PPpp') : 'N/A'}
                {timestamp && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>
                    ({formatDistanceToNow(timestamp, { addSuffix: true })})
                  </span>
                )}
              </Typography>
            }
            sx={{ py: 1, px: 2 }}
          />
          <Divider />
          <CardContent sx={{ py: 1, px: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {logData.details}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    // Desktop view
    return (
      <TableRow hover key={log.id}>
        <TableCell>
          {timestamp ? (
            <Box>
              <div>{format(timestamp, 'PPpp')}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                {formatDistanceToNow(timestamp, { addSuffix: true })}
              </div>
            </Box>
          ) : 'N/A'}
        </TableCell>
        <TableCell>{logData.title}</TableCell>
        <TableCell>
          {renderStatusChip(logData.status)}
        </TableCell>
        <TableCell>
          {log.invoiceNumber && log.invoiceNumber !== 'unknown' 
            ? log.invoiceNumber 
            : 'N/A'}
        </TableCell>
        <TableCell style={{ whiteSpace: 'pre-wrap' }}>
          {logData.details}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? 'none' : '800px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Xero Logs</Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={loadAllLogs} 
                disabled={loading.webhooks || loading.syncs || loading.refreshes}
                size={isMobile ? 'small' : 'medium'}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size={isMobile ? 'small' : 'medium'}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            mt: 1,
            mb: 1,
            '& .MuiTabs-flexContainer': {
              justifyContent: 'space-between',
            },
          }}
        >
          <Tab 
            value="webhooks" 
            label={
              <Box display="flex" alignItems="center">
                <WebhookIcon fontSize="small" sx={{ mr: 0.5 }} />
                <span>Webhooks</span>
                {loading.webhooks && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            } 
            sx={{ minHeight: 48, py: 0 }}
          />
          <Tab 
            value="syncs" 
            label={
              <Box display="flex" alignItems="center">
                <SyncIcon fontSize="small" sx={{ mr: 0.5 }} />
                <span>Syncs</span>
                {loading.syncs && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            } 
            sx={{ minHeight: 48, py: 0 }}
          />
          <Tab 
            value="refreshes" 
            label={
              <Box display="flex" alignItems="center">
                <CachedIcon fontSize="small" sx={{ mr: 0.5 }} />
                <span>Refreshes</span>
                {loading.refreshes && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            } 
            sx={{ minHeight: 48, py: 0 }}
          />
        </Tabs>
        

      </DialogTitle>
      
      <DialogContent dividers style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : currentLogs.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            height="200px"
            textAlign="center"
            p={2}
          >
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              No {activeTab} found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No ${activeTab} have been recorded yet
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1, height: '100%', overflow: 'auto' }}>
            {currentLogs
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((log) => renderLogDetails(log))}
          </Box>
        ) : (
          <>
            <TableContainer style={{ maxHeight: 'calc(100% - 53px)', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentLogs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((log) => renderLogDetails(log))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      
      {currentLogs.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={currentLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        />
      )}
      
      <DialogActions sx={{ p: 2, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<CloudSyncIcon />}
          onClick={handleManualSyncOpen}
          color="primary"
          variant="contained"
          size={isMobile ? 'medium' : 'small'}
          disabled={loading.manualSync}
        >
          {loading.manualSync ? 'Syncing...' : 'Manual Sync'}
        </Button>
        <Button 
          onClick={onClose} 
          color="primary" 
          variant="outlined"
          size={isMobile ? 'medium' : 'small'}
        >
          Close
        </Button>
      </DialogActions>
      
      {/* Manual Sync Dialog */}
      <Dialog open={manualSyncDialogOpen} onClose={handleManualSyncClose} maxWidth="xs" fullWidth>
        <DialogTitle>Manual Xero Invoice Sync</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Specify how many months of invoices to sync from Xero.
          </Typography>
          <TextField
            label="Months"
            type="number"
            fullWidth
            value={manualSyncMonths}
            onChange={handleManualSyncChange}
            inputProps={{ min: 1, max: 24 }}
            helperText="Enter a value between 1 and 24 months"
            margin="normal"
          />
          {manualSyncResult && (
            <Box mt={2} p={1.5} bgcolor={manualSyncResult.success ? 'success.light' : 'error.light'} borderRadius={1}>
              <Typography variant="body2" color={manualSyncResult.success ? 'success.dark' : 'error.dark'}>
                {manualSyncResult.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleManualSyncClose}>Cancel</Button>
          <Button 
            onClick={triggerManualSync} 
            color="primary" 
            variant="contained"
            disabled={loading.manualSync}
            startIcon={loading.manualSync ? <CircularProgress size={16} /> : <CloudSyncIcon />}
          >
            {loading.manualSync ? 'Syncing...' : 'Start Sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default XeroLogsDialog;
